/**
 * What a thing can arrive as, and what you can ask to be told about.
 *
 * One list, because three screens used to hold their own: the detail screen
 * decided which pending rows were worth drawing, the home screen named the
 * slots it found, and neither agreed on what a book's `primary` slot is
 * called. It is a hardback. Say so once.
 *
 * Pure and data-only so `derive.ts` can filter on it without pulling in a
 * component, and so the tests can argue with the rules rather than the icons.
 */

import type { DateSlot, Medium } from '../../poller/types.js';

/**
 * Which slots can exist at all, per medium. Listing "Audiobook — not
 * announced" under a film is noise pretending to be information.
 */
export const FORMAT_SLOTS: Record<Medium, readonly DateSlot[]> = {
  book: ['primary', 'paperback', 'ebook', 'audiobook'],
  film: ['primary', 'digital', 'physical', 'streaming'],
  tv: ['primary', 'streaming', 'physical'],
};

const LABELS: Record<Medium, Partial<Record<DateSlot, string>>> = {
  book: {
    primary: 'Hardback',
    paperback: 'Paperback',
    ebook: 'Ebook',
    audiobook: 'Audiobook',
  },
  film: {
    primary: 'In cinemas',
    digital: 'Digital',
    physical: 'Disc',
    streaming: 'Streaming',
  },
  tv: {
    // Tense-neutral on purpose: this label sits beside future dates on the
    // slate as well as past ones, and "First aired 4 Aug" three days before
    // the 4th reads as a claim that already happened.
    primary: 'Air date',
    streaming: 'Streaming',
    physical: 'Disc',
  },
};

/** "Hardback", "In cinemas" — what this slot is called for this medium. */
export function formatLabel(slot: DateSlot, medium: Medium): string {
  return LABELS[medium][slot] ?? slot;
}

/**
 * Does a release in `slot` count for someone who asked for `chosen`?
 *
 * Empty means everything, the same rule `subscriptions.event_types` uses:
 * nobody picks formats at add time, and an untouched subscription must mean
 * "tell me about any of it" rather than "tell me about none of it".
 */
export function wantsFormat(chosen: readonly DateSlot[], slot: DateSlot): boolean {
  return chosen.length === 0 || chosen.includes(slot);
}

/**
 * The chosen formats, said in words: "audiobook only", "hardback or ebook".
 * Used wherever the app has quietly ignored a release on your behalf — a
 * filter you cannot see is indistinguishable from a bug.
 */
export function formatsSentence(chosen: readonly DateSlot[], medium: Medium): string | null {
  if (chosen.length === 0) return null;
  const words = chosen.map((slot) => formatLabel(slot, medium).toLowerCase());
  if (words.length === 1) return `${words[0]} only`;
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}
